/**
 * 健康评估与风险等级卡片组件
 * 卡片3：展示分析结论、风险等级、病因推演和紧迫性
 * 职责：展示"意味着什么" (Assessment Conclusion)
 */

import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Stethoscope,
  AlertTriangle,
  Lightbulb,
  ShieldCheck,
  Sparkles,
  Brain,
} from "lucide-react";

export interface AssessmentCardProps {
  assessmentTitle?: string;
  diseaseCode?: string;
  severity?: "low" | "medium" | "high" | "none";
  rootCauses?: string[];
  urgencyTeaser?: string;
  sopSource?: {
    disease_code: string;
    sop_version?: string;
    is_fallback: boolean;
  };
  readinessScore?: number;
  isLoading?: boolean;
  waterType?: "freshwater" | "saltwater" | null;
}

/** 病因图标映射 */
const getCauseIcon = (cause: string): string => {
  const lowerCause = cause.toLowerCase();
  if (
    lowerCause.includes("温") ||
    lowerCause.includes("冷") ||
    lowerCause.includes("热") ||
    lowerCause.includes("temp")
  )
    return "🌡️";
  if (
    lowerCause.includes("水质") ||
    lowerCause.includes("氨") ||
    lowerCause.includes("亚硝酸") ||
    lowerCause.includes("water")
  )
    return "💧";
  if (
    lowerCause.includes("细菌") ||
    lowerCause.includes("感染") ||
    lowerCause.includes("病原") ||
    lowerCause.includes("bacter")
  )
    return "🦠";
  if (
    lowerCause.includes("寄生") ||
    lowerCause.includes("虫") ||
    lowerCause.includes("parasite")
  )
    return "🐛";
  if (
    lowerCause.includes("喂") ||
    lowerCause.includes("食") ||
    lowerCause.includes("饲料") ||
    lowerCause.includes("feed")
  )
    return "🍽️";
  if (
    lowerCause.includes("氧") ||
    lowerCause.includes("缺氧") ||
    lowerCause.includes("oxygen")
  )
    return "💨";
  if (
    lowerCause.includes("压力") ||
    lowerCause.includes("应激") ||
    lowerCause.includes("stress")
  )
    return "⚡";
  if (lowerCause.includes("光") || lowerCause.includes("light")) return "☀️";
  if (lowerCause.includes("免疫") || lowerCause.includes("immun")) return "🛡️";
  if (lowerCause.includes("换水") || lowerCause.includes("老化")) return "🔄";
  return "🔍";
};

/** 星级评分组件 */
const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex justify-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`text-xs ${
            star <= rating ? "text-amber-400 fill-amber-400" : "text-muted/20"
          }`}
        >
          ★
        </span>
      ))}
    </div>
  );
};

/** 风险等级圆环组件 */
interface RiskGaugeProps {
  severity?: "low" | "medium" | "high" | "none";
}

function RiskGauge({ severity }: RiskGaugeProps) {
  const { t } = useTranslation("assessments");

  const getConfig = (sev?: string) => {
    switch (sev) {
      case "high":
        return {
          progress: 90,
          color: "#f43f5e", // rose-500
          icon: "⚡",
          label: t("pipeline.diagnosis.riskLevel.high", "高风险"),
        };
      case "medium":
        return {
          progress: 60,
          color: "#f59e0b", // amber-500
          icon: "⚠️",
          label: t("pipeline.diagnosis.riskLevel.medium", "中风险"),
        };
      case "low":
        return {
          progress: 30,
          color: "#10b981", // emerald-500
          icon: "✓",
          label: t("pipeline.diagnosis.riskLevel.low", "低风险"),
        };
      case "none":
        return {
          progress: 100,
          color: "#10b981", // emerald-500
          icon: "✨",
          label: t("pipeline.diagnosis.wellness.title", "健康状态良好"),
        };
      default:
        return {
          progress: 0,
          color: "#6b7280",
          icon: "?",
          label: t("pipeline.result.severity.unknown", "未知"),
        };
    }
  };

  const config = getConfig(severity);
  const radius = 55;
  const strokeWidth = 6; // Thinner stroke
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset =
    circumference - (config.progress / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 128 128">
          {/* 背景圆环 */}
          <circle
            cx="64"
            cy="64"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-muted/10 dark:text-muted/20"
          />
          {/* 进度圆环 */}
          <circle
            cx="64"
            cy="64"
            r={radius}
            fill="none"
            stroke={config.color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        {/* 中心内容 */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl mb-1">{config.icon}</span>
          <span
            className={`text-sm font-bold ${
              severity === "none" ? "text-foreground" : ""
            }`}
            style={severity === "none" ? {} : { color: config.color }}
          >
            {config.label}
          </span>
        </div>
      </div>
    </div>
  );
}

export function AssessmentCard({
  assessmentTitle,
  diseaseCode,
  severity,
  rootCauses = [],
  urgencyTeaser,
  sopSource,
  readinessScore,
  isLoading = false,
  waterType,
}: AssessmentCardProps) {
  const { t } = useTranslation("assessments");

  if (isLoading) {
    return (
      <Card className="w-full md:w-[320px] lg:w-[360px] aspect-square md:aspect-auto md:h-[320px] lg:h-[360px] shrink-0 overflow-hidden border border-white dark:border-border bg-card p-0 gap-0 shadow-[0_0_12px_rgba(0,0,0,0.08)]">
        <CardContent className="p-4 sm:p-6 space-y-4 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-5 w-8" />
          </div>
          <div className="flex justify-center py-4">
            <Skeleton className="h-32 w-32 rounded-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // 判断是否为 "Wellness Mode" (健康模式)
  const isWellnessMode = severity === "none" || severity === "low";

  // Wellness Mode 相关的颜色配置
  const wellnessTheme = {
    bg: "bg-transparent", // Lightweight style (no fill)
    border: "border-emerald-500/20",
    textKey: "text-emerald-400",
    text: "text-foreground", // Main text matches body color
    title: "text-foreground", // Title keeps neutral
    iconColor: "text-yellow-400",
  };

  // Star Rating Logic: 5 stars for none, 4 for low. Hidden for others (which won't render this section anyway)
  const starRating = severity === "none" ? 5 : 4;

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
      className="w-full md:w-auto"
    >
      <Card className="w-full md:w-[320px] lg:w-[360px] md:min-h-[320px] lg:min-h-[360px] md:max-h-[640px] lg:max-h-[720px] shrink-0 overflow-hidden border border-white dark:border-border bg-card p-0 gap-0 shadow-[0_0_12px_rgba(0,0,0,0.08)]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 pb-3 border-b border-border/40">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-4 w-4 text-purple-500" />
            <CardTitle className="text-base">
              {t("pipeline.diagnosis.title", "健康评估与风险等级")}
            </CardTitle>
          </div>
        </div>

        <ScrollArea className="max-h-[calc(720px-5rem)]">
          <CardContent className="p-4 sm:p-6 pt-3">
            {/* 识别结论 */}
            {assessmentTitle && !isWellnessMode && (
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-foreground mb-1">
                  {assessmentTitle}
                </h3>
              </div>
            )}

            {/* 风险等级圆环 */}
            <div className="mb-6 mt-2">
              <RiskGauge severity={severity} />
            </div>

            {/* Middle Section: Health Indicators OR Root Cause */}
            {isWellnessMode ? (
              // Wellness Mode: Health Indicators
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider flex items-center gap-1.5 justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
                  {t("pipeline.diagnosis.wellness.indicators", "各项指标正常")}
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-muted/30 p-2.5 rounded-lg border border-border/40 text-center">
                    <span className="text-[10px] text-muted-foreground block mb-1">
                      {t(
                        "pipeline.diagnosis.indicators.bodyCondition",
                        "体表完整度",
                      )}
                    </span>
                    <StarRating rating={starRating} />
                  </div>
                  <div className="bg-muted/30 p-2.5 rounded-lg border border-border/40 text-center">
                    <span className="text-[10px] text-muted-foreground block mb-1">
                      {t(
                        "pipeline.diagnosis.indicators.coloration",
                        "体色活力",
                      )}
                    </span>
                    <StarRating rating={5} />
                  </div>
                </div>
              </div>
            ) : (
              // Alert Mode: Root Cause Analysis
              rootCauses.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
                    {t("pipeline.diagnosis.rootCauseAnalysis", "推测诱因")}
                  </h4>
                  <div className="space-y-1.5">
                    {rootCauses.map((cause, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 p-2 rounded-lg bg-muted border border-border"
                      >
                        <span className="text-base flex-shrink-0">
                          {getCauseIcon(cause)}
                        </span>
                        <p className="text-xs text-foreground flex-1 leading-relaxed">
                          {cause}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}

            {/* Footer Section: Daily Care Tip OR Risk Warning */}
            {isWellnessMode ? (
              // Wellness Mode: Daily Care Tip (Lightweight Style, Context Aware)
              <motion.div
                className={`mt-4 p-3 rounded-lg border border-solid ${wellnessTheme.border}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-start gap-2.5">
                  <div className="mt-0.5 shrink-0">
                    <Lightbulb
                      className={`h-4 w-4 ${wellnessTheme.iconColor}`}
                    />
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-xs font-bold ${wellnessTheme.title} mb-1`}
                    >
                      {t(
                        "pipeline.diagnosis.wellness.dailyTipTitle",
                        "日常养护建议",
                      )}
                    </p>
                    <p
                      className={`text-[11px] ${wellnessTheme.text} leading-relaxed opacity-90`}
                    >
                      {waterType === "freshwater"
                        ? t(
                            "pipeline.diagnosis.wellness.tips.freshwater",
                            "建议定期更换困过的水，保持水质清新。",
                          )
                        : waterType === "saltwater"
                          ? t(
                              "pipeline.diagnosis.wellness.tips.saltwater",
                              "建议定期检测盐度比重，维持海缸稳定。",
                            )
                          : t(
                              "pipeline.diagnosis.wellness.tips.default",
                              "保持水质稳定和日常观察，是维持爱鱼健康的关键。",
                            )}
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : (
              // Alert Mode: Risk Warning
              urgencyTeaser && (
                <motion.div
                  className="mt-4 p-3 bg-rose-50 dark:bg-rose-950/10 border border-rose-200 dark:border-rose-900/30 rounded-lg"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-rose-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-bold text-rose-800 dark:text-rose-300 mb-0.5">
                        {t("pipeline.diagnosis.riskWarning", "风险提示")}
                      </p>
                      <p className="text-[11px] text-rose-700 dark:text-rose-400/80 leading-relaxed">
                        {urgencyTeaser}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )
            )}
          </CardContent>
        </ScrollArea>
      </Card>
    </motion.div>
  );
}

export default AssessmentCard;
